import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Route, Routes, useLocation } from 'react-router'

import { Logo } from '@/components/Logo'
import { App } from '@/App'
import { OnboardingGate } from '@/lib/onboardingGate'
import { RealtimeProvider } from '@/lib/realtime'
import { SessionProvider } from '@/lib/session'
import { watchSystemTheme } from '@/lib/theme'
import { Auth } from '@/routes/Auth'
import { Invitacion } from '@/routes/Invitacion'
import { NuevaContrasena, PedirEnlace } from '@/routes/Recuperar'
import '@/styles/index.css'

// El tema «automático» tiene que seguir al sistema mientras la app está
// abierta. El oyente vive aquí y no dentro de una pantalla: si dependiera de
// que Ajustes esté montado, que el teléfono pasara a oscuro por la noche no
// repintaría nada.
watchSystemTheme(() => undefined)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Los datos llegan por socket en cuanto cambian, así que reconsultar al
      // volver a la ventana solo gastaría red sin traer nada nuevo.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
  },
})

function Root() {
  // Fuerza a reconsultar la sesión tras entrar o registrarse.
  const [, setNonce] = useState(0)

  // Las pantallas que van **antes** del portal de sesión.
  //
  // La invitación, porque quien la abre normalmente no tiene cuenta todavía: si
  // pasara por el portal vería el acceso, se registraría creando su propio
  // hogar y acabaría dentro de la app sin haber aceptado nada.
  //
  // La recuperación, porque quien olvidó su contraseña por definición no puede
  // entrar; ponerla detrás del acceso la haría inalcanzable justo para quien la
  // necesita.
  //
  // Se lee de `useLocation` y no de `window.location`: con la segunda, este
  // componente no vuelve a evaluarse al navegar, así que el enlace «¿Olvidaste
  // tu contraseña?» cambiaría la barra de direcciones sin cambiar la pantalla.
  const { pathname } = useLocation()
  if (pathname.startsWith('/invitacion/') || pathname.startsWith('/recuperar')) {
    return (
      <Routes>
        <Route path="/invitacion/:token" element={<Invitacion />} />
        <Route path="/recuperar" element={<PedirEnlace />} />
        <Route path="/recuperar/:token" element={<NuevaContrasena />} />
      </Routes>
    )
  }

  return (
    <SessionProvider
      loading={<PantallaCarga />}
      fallback={
        <Auth
          onAuthenticated={() => {
            void queryClient.invalidateQueries({ queryKey: ['session'] })
            setNonce((n) => n + 1)
          }}
        />
      }
    >
      <RealtimeProvider>
        <OnboardingGate>
          <App />
        </OnboardingGate>
      </RealtimeProvider>
    </SessionProvider>
  )
}

/**
 * Pantalla de carga sobria: solo la marca sobre el fondo del tema.
 * Un spinner que aparece durante 200 ms se percibe como un parpadeo molesto;
 * un lienzo quieto se lee como que la app ya está, aunque tarde lo mismo.
 */
function PantallaCarga() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg">
      <Logo className="size-12 animate-pulse" />
    </div>
  )
}

const container = document.getElementById('root')
if (!container) throw new Error('No se encontró el nodo raíz')

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Root />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
